/*
XOL-67 (B6, v2.1 V6): Step 5 of an RTA, Finalize, in pure functions. No Vue, no store, no axios: RtaCompletion and RtaDetails
wrap them, and the specs drive them with plain objects.

  The values (plan 1.5; API E18 / E19)
    effective(defaults, values)           the form the closer sees: the server's defaults under the saved overlay
    toValues(eff, pristine, saved, opts)  the SPARSE overlay to save: only what differs from the defaults (opts.rtsChoice: the RTS section is the one key `linkRtsId`)
    derivedRtsSummary(siteName)           the RTS summary that follows the site name until it is edited
    precheck(eff, rts, opts)              advisory checks before the Complete confirm: { errors, keys } (opts.rtsChoice skips the RTS fields)
    sameValues(a, b)                      do two overlays say the same thing?

  Errors
    mapServerErrors(failure)              a 422 completion_invalid / site_name_taken / site_id_taken -> { errors, unmapped }
    fieldLabel(key), tabOf(key), tabName(tab), tabErrors(errors), firstErrorTab(errors)
    errorSummary(errors)                  "Fix 2 field(s) before completing: Site Name (Details), Target Date (RTS)."

  The RTS choice (XOL-693, decision 211)
    rtsChoiceOf(completion)               the API's `completion.rtsChoice`, normalised, or null (today's RTS form)
    rtsOutcomeOf(completion)              'opened' | 'linked' | 'none' | null (a null outcome with an `rtsId` reads as 'opened')
    rtsRef(id)                            "RTS #2190"
    rtsWhyText, rtsHintText, rtsNoneText, rtsCandidateRows, rtsChangedText, rtsStaleText, rtsStaleUnreadText   the words of the RTS section

  Copy (UX v3 sections 5 to 8)
    completeConfirm(ctx), completeSentence(ctx), staleText(), staleCompleteText(), invalidFallback()
    finalOptionRows(option, ctx), otherOptionNos(options, finalId), structureLabel(list, id), structurePath(list, id)

The contract is the API plan E18 / E19: the overlay is { site, structure, finance, tenancy, rts }; "an unknown key is a 422" (DEV-16), so
`toValues` only ever writes the fields the Step 5 forms edit. `rts` is for a Telco client only.

XOL-693 (decision 211): a new RTS is opened at Complete only when the client has no blocking sharer on the kept site AND no open RTS there.
Otherwise the API sends `completion.rtsChoice` and the RTS section is a two-way choice, in ONE overlay key, `rts.linkRtsId`: an integer links
that RTS, null completes without one. The RTS form fields are never sent with a choice, and `linkRtsId` is never sent without one. An absent
`rtsChoice` / `rtsOutcome` (an API that predates XOL-693) is today's behaviour.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { HANDOVER_TRANSITION_STATUSES } from '@/components/Data'
import { approvedOptionRows } from '@/lib/rtaDecision'
import { ymd } from '@/lib/siteTransition'

export var SECTIONS = ['site', 'structure', 'finance', 'tenancy', 'rts']

// The fields of each section the Step 5 forms edit (SiteAction.vue's tabs and RequestCreateFields). `status` is not one: an RTA
// always creates a Candidate site and the server forces it (decision 78), and a nightly API job moves it to `handoverStatus` once
// its `handover` date has arrived (decisions 83, 85). `active` is not one either (decision 101): Step 5 stops sending it, and the
// job sets the Active date. `region`, `state`, `district` and `subdistrict` come from the locality picker (SiteAction.onSaveDialog's `loc`).
export var SITE_FIELDS = ['siteName', 'siteId', 'siteGroup', 'coverage', 'handover', 'handoverStatus', 'address', 'postCode', 'latitude', 'longitude', 'agl', 'region', 'state', 'district', 'subdistrict']
export var STRUCTURE_FIELDS = ['structure_id', 'supplier_id', 'height', 'weight', 'width', 'depth']
export var FINANCE_FIELDS = ['capex', 'opex']
export var RTS_FIELDS = ['actualDate', 'targetDate', 'priority', 'summary', 'remarks']
// XOL-693: the RTS section's one key when the API offers a choice. It is never one of RTS_FIELDS (the RTS form's fields).
export var RTS_LINK = 'linkRtsId'

// Tenancy: the `tenancy` table's columns that a site is created with (Tenancy::$fillable, bar `site_id` and the stamps), plus `contact`,
// `contact_id` and `renewal` (DEV-27). UpdateTenancy's object holds more than that and the API refuses an unknown key (DEV-16): it binds
// `width` and `depth` (the Plot Width / Depth, which are not tenancy columns) and can set `expiry` (a renewal's, not the tenancy's), and it
// carries `owner` (the landowner organisation) and `count`. Sites -> Add drops those silently; Step 5 must not send them.
export var TENANCY_FIELDS = [
  'owner_id', 'contact_id', 'landTitleRef', 'landType', 'landStatus', 'classificationType', 'landValue',
  'started', 'increment', 'months', 'depositMonths', 'deposit', 'depositDate',
  'frequency', 'taxable', 'autorenew', 'renewYears',
  'util_id', 'utilAccount', 'utilAddress', 'utilDeposit', 'utilDepositDate', 'remarks',
  'contact', 'renewal'
]

// DEV-27: what the API keeps of a new contact and of the renewal; it drops the rest, and the SPA does not send it.
export var CONTACT_FIELDS = ['name', 'shortName', 'jobTitle', 'phone', 'email', 'remarks']
export var RENEWAL_FIELDS = ['months', 'amount', 'increment', 'taxable', 'tax', 'total', 'started', 'expiry', 'remarks']

var FIELDS = { site: SITE_FIELDS, structure: STRUCTURE_FIELDS, finance: FINANCE_FIELDS, tenancy: TENANCY_FIELDS, rts: RTS_FIELDS }

// SiteRules::create (API): the advisory lengths.
export var SITE_NAME_MIN = 5
export var SITE_NAME_MAX = 120
export var SITE_ID_MIN = 4
export var SITE_ID_MAX = 16
// rts.summary: WorkflowFields::COLUMN_VALUE_RULES `max:150` (API DEV-48). The field stops typing here (maxlength + counter) and the precheck says so.
export var SUMMARY_MAX = 150

function blank (v) {
  return v === undefined || v === null || v === ''
}

function isObject (v) {
  return v !== null && typeof v === 'object'
}

function copy (v) {
  return isObject(v) ? JSON.parse(JSON.stringify(v)) : v
}

/** Do two field values say the same thing? Null, undefined and '' are the same (nothing); 3 and '3' are the same; objects by content. */
export function sameValue (a, b) {
  if (blank(a) && blank(b)) {
    return true
  }

  if (blank(a) || blank(b)) {
    return false
  }

  if (isObject(a) || isObject(b)) {
    return JSON.stringify(a) === JSON.stringify(b)
  }

  return String(a) === String(b)
}

/** The server's defaults under the saved overlay, per section: the form the closer sees. Copies; the inputs are untouched. */
export function effective (defaults, values) {
  var d = isObject(defaults) ? defaults : {}
  var v = isObject(values) ? values : {}
  var out = {}

  SECTIONS.forEach(function (section) {
    var base = isObject(d[section]) ? copy(d[section]) : {}
    var over = isObject(v[section]) ? v[section] : {}

    Object.keys(over).forEach(function (k) {
      base[k] = copy(over[k])
    })

    out[section] = base
  })

  return out
}

/**
 * The summary of the RTS follows the site name until the closer writes one of their own: the server's default is
 * "RTS for {siteName}" of the REQUEST's name, and it does not follow an edit, so the SPA writes it (plan 1.5).
 */
export function derivedRtsSummary (siteName) {
  return blank(siteName) ? 'RTS' : 'RTS for ' + String(siteName).trim()
}

function sectionKeys (section, saved) {
  var keys = FIELDS[section].slice()

  // What was saved before stays in the overlay's reach, so a key this version does not edit is not silently dropped.
  Object.keys(saved || {}).forEach(function (k) {
    if (keys.indexOf(k) === -1) {
      keys.push(k)
    }
  })

  return keys
}

function pick (obj, keys) {
  var out = {}

  if (!isObject(obj)) {
    return out
  }

  keys.forEach(function (k) {
    if (!blank(obj[k])) {
      out[k] = copy(obj[k])
    }
  })

  return out
}

// DEV-27, on this side: an existing contact is sent as `contact_id` alone (the API drops `contact` then); a new one as its six fields; the
// renewal as its nine. UpdateTenancy puts whole selected-contact objects and the whole renewal row in them.
function sanitiseTenancy (piece, form) {
  if ('contact' in piece) {
    var contactId = form.contact_id

    if (!blank(contactId) || blank(piece.contact)) {
      delete piece.contact
    } else {
      var contact = pick(piece.contact, CONTACT_FIELDS)

      if (Object.keys(contact).length > 0) {
        piece.contact = contact
      } else {
        delete piece.contact
      }
    }
  }

  if ('renewal' in piece) {
    var renewal = pick(piece.renewal, RENEWAL_FIELDS)

    if (Object.keys(renewal).length > 0) {
      piece.renewal = renewal
    } else {
      delete piece.renewal
    }
  }

  return piece
}

/**
 * The sparse overlay to save: `form` is the effective form as it stands (sections site, structure, finance, tenancy and, for a
 * Telco client, rts), `pristine` the same built from the defaults alone, `saved` the overlay the server holds. A field is in the
 * overlay when it differs from the default. Setting one back to its default drops it. A section with nothing in it is left out.
 * The rts section is left out when `form.rts` is absent (a Tower Co client has none: the API refuses it).
 *
 * XOL-693: `opts.rtsChoice` (the API offered the link / without choice) makes the rts section's own key `linkRtsId` only: the RTS form is
 * hidden, so nothing is ever read from it. The RTS form fields an earlier save holds (`saved.rts`, bar `linkRtsId`) are carried through
 * UNCHANGED (decision 225), so they come back over the defaults if the choice later goes away; being the saved values they never differ
 * from what the server holds, so they cause no save of their own. Without a choice the rts section is the form's fields as before and
 * `linkRtsId` is never sent: a saved one is dropped on the next save. "Complete without an RTS" over a candidate default is
 * `linkRtsId: null`; an untouched choice sends nothing.
 */
export function toValues (form, pristine, saved, opts) {
  var out = {}
  var f = isObject(form) ? form : {}
  var p = isObject(pristine) ? pristine : {}
  var s = isObject(saved) ? saved : {}
  var choice = !!(opts && opts.rtsChoice)

  SECTIONS.forEach(function (section) {
    if (!isObject(f[section])) {
      return
    }

    var base = isObject(p[section]) ? p[section] : {}
    var piece = {}

    var keys = sectionKeys(section, s[section])

    if (section === 'rts') {
      keys = choice ? [RTS_LINK] : keys.filter(function (k) { return k !== RTS_LINK })
    }

    keys.forEach(function (k) {
      if (!sameValue(f[section][k], base[k])) {
        piece[k] = blank(f[section][k]) ? null : copy(f[section][k])
      }
    })

    if (section === 'tenancy') {
      piece = sanitiseTenancy(piece, f[section])
    }

    if (section === 'rts' && choice) {
      Object.keys(isObject(s.rts) ? s.rts : {}).forEach(function (k) {
        if (k !== RTS_LINK) {
          piece[k] = copy(s.rts[k])
        }
      })
    }

    if (Object.keys(piece).length > 0) {
      out[section] = piece
    }
  })

  return out
}

/** Do two overlays say the same thing? */
export function sameValues (a, b) {
  var x = isObject(a) ? a : {}
  var y = isObject(b) ? b : {}

  return SECTIONS.every(function (section) {
    var p = isObject(x[section]) ? x[section] : {}
    var q = isObject(y[section]) ? y[section] : {}
    var keys = Object.keys(p).concat(Object.keys(q))

    return keys.every(function (k) { return sameValue(p[k], q[k]) })
  })
}

/**
 * Do two overlays say the same thing, KEY BY KEY? Unlike `sameValues`, an explicit null is not the same as no key: in the overlay, no key
 * means "the default" and null means "cleared". The auto-save compares with this one, or clearing a field whose default (or a kept
 * site's own value, decision 209) is not empty would be dropped as "nothing new" and never reach the server.
 */
export function sameOverlay (a, b) {
  var x = isObject(a) ? a : {}
  var y = isObject(b) ? b : {}

  return SECTIONS.every(function (section) {
    var p = isObject(x[section]) ? x[section] : {}
    var q = isObject(y[section]) ? y[section] : {}
    var keys = Object.keys(p).concat(Object.keys(q))

    return keys.every(function (k) {
      return (k in p) === (k in q) && sameValue(p[k], q[k])
    })
  })
}

/* ------------------------------------------------------------------------
   Fields, tabs and errors
------------------------------------------------------------------------ */

var LOCALITY = ['address', 'postCode', 'latitude', 'longitude', 'agl', 'region', 'state', 'district', 'subdistrict']

var LABELS = {
  'site.siteName': 'Site Name',
  'site.siteId': 'Site ID',
  'site.siteGroup': 'Site Group',
  'site.coverage': 'Coverage',
  'site.handover': 'Handover date',
  'site.handoverStatus': 'Handover status',
  'site.active': 'Active',
  'site.address': 'Address',
  'site.postCode': 'Post Code',
  'site.latitude': 'Latitude',
  'site.longitude': 'Longitude',
  'site.agl': 'AGL',
  'site.region': 'District',
  'site.state': 'District',
  'site.district': 'District',
  'site.subdistrict': 'District',
  'structure.structure_id': 'Structure Type',
  'structure.supplier_id': 'Tower Supplier',
  'structure.height': 'Structure Height',
  'structure.weight': 'Design Weight',
  'structure.width': 'Site Width',
  'structure.depth': 'Site Depth',
  'finance.capex': 'Site CAPEX',
  'finance.opex': 'Site OPEX',
  'tenancy.owner_id': 'Landlord',
  'tenancy.contact_id': 'Site Rep.',
  'tenancy.contact': 'Site Rep.',
  'tenancy.landType': 'Land Type',
  'tenancy.landStatus': 'Land Status',
  'tenancy.landValue': 'Land Value',
  'tenancy.renewal': 'Tenancy Start & End',
  'rts.actualDate': 'Actual Date',
  'rts.targetDate': 'Target Date',
  'rts.priority': 'Priority',
  'rts.summary': 'Summary',
  'rts.remarks': 'Remarks',
  // XOL-693 (G2): so the summary never reads "RTS (RTS)".
  'rts.linkRtsId': 'RTS on completion'
}

// The tab pane names of SiteForm, and the words of the summary alert. The RTS form is not a tab.
var TAB_NAMES = { site: 'Details', structure: 'Structure', locality: 'Locality', tenancy: 'Tenancy', finance: 'Finance', rts: 'RTS' }

// The order the alert lists them in, and the tab that opens first.
var TAB_ORDER = ['site', 'structure', 'locality', 'tenancy', 'finance', 'rts']

/** The section of an error key ('site.siteName' -> 'site'). */
function sectionOf (key) {
  return String(key).split('.')[0]
}

/** The tab (SiteForm pane name, or 'rts') a field's error belongs to. */
export function tabOf (key) {
  var section = sectionOf(key)
  var field = String(key).split('.')[1] || ''

  if (section === 'site') {
    return LOCALITY.indexOf(field) !== -1 ? 'locality' : 'site'
  }

  return section
}

export function tabName (tab) {
  return TAB_NAMES[tab] || tab
}

/** "Site Name", "Target Date": the label of a field; the field name itself for one this table does not know. */
export function fieldLabel (key) {
  if (LABELS[key]) {
    return LABELS[key]
  }

  var field = String(key).split('.').slice(1).join('.') || String(key)

  return field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, function (c) { return c.toUpperCase() })
}

/** The tabs that hold an error: { site: true, locality: true, rts: true }. */
export function tabErrors (errors) {
  var out = {}

  Object.keys(errors || {}).forEach(function (k) {
    out[tabOf(k)] = true
  })

  return out
}

/** The first tab with an error, in tab order (the one that opens), or null. */
export function firstErrorTab (errors) {
  var t = tabErrors(errors)

  for (var i = 0; i < TAB_ORDER.length; i++) {
    if (t[TAB_ORDER[i]] && TAB_ORDER[i] !== 'rts') {
      return TAB_ORDER[i]
    }
  }

  return null
}

function orderOf (key) {
  var i = TAB_ORDER.indexOf(tabOf(key))

  return i === -1 ? TAB_ORDER.length : i
}

/**
 * "Fix 2 field(s) before completing: Site Name (Details), Target Date (RTS)." (UX v3 section 6). '' without errors.
 * XOL-693: a BARE section key ('rts': the API's "Duplicate RTS" names no field) is not a field. It is not counted, and its message is
 * shown as the server wrote it, after the field sentence ("Fix 1 field(s) before completing: Target Date (RTS). Duplicate RTS.") or alone.
 */
export function errorSummary (errors) {
  var all = errors || {}
  var keys = Object.keys(all)

  if (keys.length === 0) {
    return ''
  }

  var fieldKeys = keys.filter(function (k) { return String(k).indexOf('.') !== -1 })
  var bareKeys = keys.filter(function (k) { return String(k).indexOf('.') === -1 })
  var sentence = ''

  if (fieldKeys.length > 0) {
    // Tab order, then the order they came in (a stable sort by hand: Babel 6 targets with no Array#sort stability guarantee).
    var ordered = fieldKeys.map(function (k, i) { return { key: k, at: i, tab: orderOf(k) } }).sort(function (a, b) {
      return a.tab - b.tab || a.at - b.at
    })
    var seen = {}
    var parts = []

    ordered.forEach(function (o) {
      var text = fieldLabel(o.key) + ' (' + tabName(tabOf(o.key)) + ')'

      if (!seen[text]) {
        seen[text] = true
        parts.push(text)
      }
    })

    sentence = 'Fix ' + fieldKeys.length + ' field(s) before completing: ' + parts.join(', ') + '.'
  }

  var said = {}
  var messages = []

  bareKeys.forEach(function (k) {
    var message = blank(all[k]) || String(all[k]).trim() === '' ? invalidFallback() : String(all[k]).trim()

    message = /[.!?]$/.test(message) ? message : message + '.'

    if (!said[message]) {
      said[message] = true
      messages.push(message)
    }
  })

  return [sentence].concat(messages).filter(function (t) { return t !== '' }).join(' ')
}

/* ------------------------------------------------------------------------
   Advisory checks (mirrors SiteRules::create; the server is authoritative)
------------------------------------------------------------------------ */

function len (v) {
  return String(v).trim().length
}

// Nothing, or only spaces.
function empty (v) {
  return blank(v) || len(v) === 0
}

/**
 * The checks the closer gets before the confirm opens: required and lengths for the site, dates and priority for the RTS. They
 * only look at presence and length (the server also checks uniqueness, coverage and the rest). `rts` is null for a Tower Co
 * client. Returns { errors: { 'site.siteName': 'Enter the Site Name.' }, count }.
 *
 * The site handover date and the handover status are both optional (decision 194, which replaces decisions 79, 84 and 106): nothing
 * scheduled, a status only, or both complete. The one refusal is a date without a status, keyed on the status. `opts.skipHandover`
 * leaves the check out: the kept legacy site has already left Candidate, so the API ignores them (decision 108; they are shown, disabled).
 *
 * XOL-693: `opts.rtsChoice` (the RTS section is the link / without choice) leaves the four RTS field checks out and adds nothing for the
 * choice: "Link" is pre-selected (decision 213), so both options are always valid, and with no candidate there is nothing to check.
 */
export function precheck (form, rts, opts) {
  var errors = {}
  var site = (form && form.site) || {}

  if (!(opts && opts.skipHandover)) {
    if (!empty(site.handoverStatus) && HANDOVER_TRANSITION_STATUSES.indexOf(site.handoverStatus) === -1) {
      errors['site.handoverStatus'] = 'Select the handover status.'
    } else if (ymd(site.handover) !== '' && empty(site.handoverStatus)) {
      // (`ymd` reads a stored 0000-00-00, as on the API, as no date.)
      // Decision 194: a date with nothing to become would never schedule anything. Short on purpose: the line sits inside the inline-block Handover group.
      errors['site.handoverStatus'] = 'Select a status, or clear the date.'
    }
  }

  if (empty(site.siteName)) {
    errors['site.siteName'] = 'Enter the Site Name.'
  } else if (len(site.siteName) < SITE_NAME_MIN || len(site.siteName) > SITE_NAME_MAX) {
    errors['site.siteName'] = 'Site Name must be ' + SITE_NAME_MIN + ' to ' + SITE_NAME_MAX + ' characters.'
  }

  if (empty(site.siteId)) {
    errors['site.siteId'] = 'Enter the Site ID.'
  } else if (len(site.siteId) < SITE_ID_MIN || len(site.siteId) > SITE_ID_MAX) {
    errors['site.siteId'] = 'Site ID must be ' + SITE_ID_MIN + ' to ' + SITE_ID_MAX + ' characters.'
  }

  if (isObject(rts) && !(opts && opts.rtsChoice)) {
    if (blank(rts.actualDate)) {
      errors['rts.actualDate'] = 'Enter an Actual Date.'
    }

    if (blank(rts.targetDate)) {
      errors['rts.targetDate'] = 'Enter the Target Date.'
    }

    if (blank(rts.priority)) {
      errors['rts.priority'] = 'Select a Priority.'
    }

    if (!blank(rts.summary) && String(rts.summary).length > SUMMARY_MAX) {
      errors['rts.summary'] = 'Summary is too long.'
    }
  }

  return { errors: errors, count: Object.keys(errors).length }
}

/**
 * A refusal of Complete or of a Step 5 save, in field errors. `failure` is lib/rtaRefusal.parseFailure: { status, code, data, errors }.
 *   site_name_taken / site_id_taken   'site.siteName' / 'site.siteId': "A site already uses this Site Name." Complete (and a Step 5 save) checks the SITES only
 *                                     (API C13, I-22); the request's own check at Step 1 also counts other open RTAs, with its own words (rtaNominal).
 *   completion_invalid                every key of `errors` ({ 'site.siteName': ['...'], 'rts.targetDate': ['...'] }), the API's
 *                                     own sentence for each. The create form's keys ('nominal.siteName') are the same fields.
 * Returns { errors, count }; empty when nothing in the answer names a field.
 */
export function mapServerErrors (failure) {
  var f = failure || {}
  var errors = {}

  function put (key, message) {
    key = String(key).replace(/^nominal\./, 'site.')

    if (key.indexOf('.') === -1 && SECTIONS.indexOf(key) === -1) {
      return
    }

    if (!errors[key]) {
      errors[key] = message
    }
  }

  function first (v) {
    if (Array.isArray(v)) {
      return v.length ? String(v[0]) : ''
    }

    return blank(v) ? '' : String(v)
  }

  if (f.code === 'site_name_taken') {
    put('site.siteName', 'A site already uses this Site Name.')
  }

  if (f.code === 'site_id_taken') {
    put('site.siteId', 'A site already uses this Site ID.')
  }

  var server = f.errors && typeof f.errors === 'object' ? f.errors : {}

  Object.keys(server).forEach(function (k) {
    var message = first(server[k])
    var key = String(k).replace(/^nominal\./, 'site.')

    if (key === 'site.siteName' && f.code === 'site_name_taken') {
      return
    }

    if (key === 'site.siteId' && f.code === 'site_id_taken') {
      return
    }

    // A bare section key ('rts': "Duplicate RTS") with no message at all has no field to point at either.
    put(k, message || (key.indexOf('.') === -1 ? invalidFallback() : 'Check this field.'))
  })

  return { errors: errors, count: Object.keys(errors).length }
}

/* ------------------------------------------------------------------------
   The RTS choice (XOL-693, decision 211): the client is already a sharer of the kept site, or has an open RTS there
------------------------------------------------------------------------ */

/** "RTS #2190" (G1). An RTA is written through rtaStage.rtaReference ("RTA-002100"): the app has no zero-padded RTS reference. */
export function rtsRef (id) {
  return 'RTS #' + id
}

function present (v) {
  return v !== undefined && v !== null
}

/**
 * The API's `completion.rtsChoice` (Tower Co callers; plan A11), normalised, or null: null is "a new RTS can be opened", today's RTS form. A
 * missing or non-object value is null, so an API that predates XOL-693 means today's form. A missing `blockedBy` is both false.
 *   { blockedBy: { sharer, openRts }, candidate: { id, status, open, summary, created_at, completed_at } | null,
 *     claimed: { id, rtaId, status, open } | null, defaultLinkId: ?int, key: ?string }
 */
export function rtsChoiceOf (completion) {
  var raw = isObject(completion) ? completion.rtsChoice : null

  if (!isObject(raw) || Array.isArray(raw)) {
    return null
  }

  var by = isObject(raw.blockedBy) ? raw.blockedBy : {}
  var c = isObject(raw.candidate) && present(raw.candidate.id) ? raw.candidate : null
  var k = isObject(raw.claimed) && present(raw.claimed.id) ? raw.claimed : null

  return {
    blockedBy: { sharer: !!by.sharer, openRts: !!by.openRts },
    candidate: c ? {
      id: c.id,
      status: present(c.status) ? c.status : null,
      open: !!c.open,
      summary: present(c.summary) ? c.summary : '',
      created_at: present(c.created_at) ? c.created_at : null,
      completed_at: present(c.completed_at) ? c.completed_at : null
    } : null,
    claimed: k ? { id: k.id, rtaId: present(k.rtaId) ? k.rtaId : null, status: present(k.status) ? k.status : null, open: !!k.open } : null,
    defaultLinkId: present(raw.defaultLinkId) ? raw.defaultLinkId : null,
    key: present(raw.key) ? String(raw.key) : null
  }
}

/**
 * How Complete treated the RTS (G6, the API's read rule): 'opened' | 'linked' | 'none', or null for a Tower Co client. A legacy row has a null
 * `rtsOutcome`: with an `rtsId` it reads as 'opened', without one as no RTS line at all.
 */
export function rtsOutcomeOf (completion) {
  var c = isObject(completion) ? completion : {}

  if (c.rtsOutcome === 'opened' || c.rtsOutcome === 'linked' || c.rtsOutcome === 'none') {
    return c.rtsOutcome
  }

  return present(c.rtsId) && c.rtsId !== '' ? 'opened' : null
}

/** The line under the RTS heading: why a new RTS can't be opened (UX copy, "Why line"). */
export function rtsWhyText (choice, telco) {
  var w = names({ telco: telco })
  var by = choice && choice.blockedBy ? choice.blockedBy : {}

  if (by.openRts && !by.sharer) {
    return 'A new RTS can\'t be opened: ' + w.telco + ' already has an open RTS on this site.'
  }

  if (by.openRts) {
    return 'A new RTS can\'t be opened: ' + w.telco + ' is already a sharer on this site and has an open RTS here.'
  }

  return 'A new RTS can\'t be opened: ' + w.telco + ' is already a sharer on this site.'
}

/** The line under the choice: what the selected option does. `mode` is 'link' or 'none'; `candidate` the RTS (choice.candidate). */
export function rtsHintText (mode, candidate) {
  var id = candidate ? rtsRef(candidate.id) : 'The RTS'

  if (mode === 'none') {
    return 'This RTA completes with no RTS. ' + id + ' stays as it is and isn\'t linked.'
  }

  if (candidate && candidate.open) {
    // An open RTS with no status to name (not a case the API sends) drops the clause rather than print "null".
    return id + ' is linked to this RTA' + (empty(candidate.status) ? '' : ' and carries on from ' + candidate.status) + '. No new RTS is opened.'
  }

  return id + ' is linked to this RTA and stays Closed. No new RTS is opened.'
}

/**
 * The text of the "No RTS is created" alert when the choice has no candidate (Variant C): the API found nothing to link
 * (`claimed` null), or the RTS it would have linked belongs to another RTA (`claimed`). `rtaRef` writes an RTA's reference
 * (rtaStage.rtaReference: this module stays free of rtaStage).
 */
export function rtsNoneText (choice, telco, rtaRef) {
  var w = names({ telco: telco })
  var claimed = choice ? choice.claimed : null
  var tail = ' Completing this RTA opens no RTS and links none.'

  if (claimed) {
    var ref = claimed.rtaId !== null && claimed.rtaId !== undefined && typeof rtaRef === 'function' ? rtaRef(claimed.rtaId) : ''
    var holder = ref ? 'belongs to ' + ref : 'belongs to another RTA'

    if (claimed.open) {
      return 'A new RTS can\'t be opened, because ' + w.telco + ' already has an open RTS on this site: ' + rtsRef(claimed.id) + ', which ' + holder + '.' + tail
    }

    return 'A new RTS can\'t be opened, because ' + w.telco + ' is already a sharer on this site. Its RTS here, ' + rtsRef(claimed.id) + ', ' + holder + '.' + tail
  }

  return 'A new RTS can\'t be opened, because ' + w.telco + ' is already a sharer on this site, and it has no RTS here that can be linked. Completing this RTA opens no RTS.'
}

/** The rows of the linked RTS's card (RtaOptionSummary): [{ label, value }]. `fmtDate` shows a date as the app does. */
export function rtsCandidateRows (candidate, fmtDate) {
  var c = candidate || {}
  var f = typeof fmtDate === 'function' ? fmtDate : function (d) { return d }
  var status = ''

  if (c.open) {
    status = 'Open' + (empty(c.status) ? '' : ', at ' + c.status)
  } else {
    status = 'Closed' + (empty(c.completed_at) ? '' : ' on ' + f(c.completed_at))
  }

  return [
    { label: 'Status', value: status },
    { label: 'Opened', value: empty(c.created_at) ? '' : f(c.created_at) },
    { label: 'Summary', value: empty(c.summary) ? '' : String(c.summary) }
  ]
}

/** The notice when the RTS section changed under the closer (the key watcher). `from` and `to`: 'form' (a new RTS can be opened) or 'choice'. */
export function rtsChangedText (from, to, telco) {
  var w = names({ telco: telco })

  if (from === 'form' && to === 'choice') {
    return w.telco + ' now has a sharer or an open RTS on this site, so a new RTS can\'t be opened. The RTS section now shows what you can do instead.'
  }

  if (from === 'choice' && to === 'form') {
    return 'A new RTS can now be opened for ' + w.telco + ', so Step 5 shows the RTS form again.'
  }

  return w.telco + '\'s sharer or RTS on this site changed. The RTS section now shows the latest.'
}

/** The same notice when Step 5 could not be read again (decision 229): it must not claim the section shows the latest. */
export function rtsStaleUnreadText (telco) {
  var w = names({ telco: telco })

  return w.telco + '\'s sharer or RTS on this site changed since you opened Step 5, but Step 5 couldn\'t be read again. Reload the page and try again.'
}

/** The notice when Complete was refused for a changed RTS choice (409 stale_rts_choice). */
export function rtsStaleText (telco) {
  var w = names({ telco: telco })

  return w.telco + '\'s sharer or RTS on this site changed since you opened Step 5. The RTS section now shows the latest; check it and press Complete RTA again.'
}

/* ------------------------------------------------------------------------
   Copy
------------------------------------------------------------------------ */

function names (n) {
  n = n || {}

  return {
    telco: n.telco || 'the client',
    towerCo: n.towerCo || 'Tower Co'
  }
}

// Under a pre-filled Site Name / Site ID (UX spec 2.8, mock M-27): the request's (Step 1), or, for an open legacy RTA whose Candidate site is kept, that
// site's own record. The handover status says "From option {n}." (RtaCompletion builds it: the number is its own).
export var NOTE_REQUEST = 'From the request.'
export var NOTE_LEGACY = 'From the legacy site record.'

/** The C-12 card's body when another closer saved Step 5 first (UX v3 section 6). */
export function staleText () {
  return 'Someone changed Step 5 while you were editing. Their version is now shown, and your last change wasn\'t saved.'
}

/** The same, when it was Complete RTA that was refused for a stale version (the closer was not editing). */
export function staleCompleteText () {
  return 'Someone changed Step 5 while you were completing. Their version is now shown; check it and press Complete RTA again.'
}

/** The toast when a refusal names no field at all (UX v3 section 8). */
export function invalidFallback () {
  return 'The site or RTS details aren\'t valid. Check the fields marked in Step 5.'
}

/** The line the lock note carries for a Tower Co user who may not fill Step 5 in. */
export function readOnlyNote () {
  return 'Only a project, finance or admin manager can fill in Step 5 and complete the RTA.'
}

/** The note under the Complete RTA button. */
export function completeHint () {
  return 'Checks the site and RTS details, then asks you to confirm.'
}

/** "Options 2, 3 and 4" style list: [2, 3, 4] -> "2, 3 and 4". */
function listNos (nos) {
  if (nos.length <= 1) {
    return nos.join('')
  }

  return nos.slice(0, -1).join(', ') + ' and ' + nos[nos.length - 1]
}

/**
 * The option numbers Complete marks Not Selected (API RtaFinisher::closeOptions): every option but the final one, except those that already are
 * Not Selected. A Draft, Review, Approved-elsewhere or DECLINED option all become Not Selected. `options` is the list rows { id, optionNo, optionStatus }.
 */
export function otherOptionNos (options, finalId) {
  var out = []

  ;(options || []).forEach(function (o) {
    if (!o || (finalId !== null && finalId !== undefined && Number(o.id) === Number(finalId))) {
      return
    }

    if (['Not Selected', 'Final Approved'].indexOf(o.optionStatus) !== -1) {
      return
    }

    out.push(Number(o.optionNo))
  })

  return out.sort(function (a, b) { return a - b })
}

/**
 * Is the handover date today or earlier, with a handover status chosen (decision 86)? True only when all three are present: then the
 * site changes at the next nightly run, not on save, and the form warns. The caller passes `formatDateSql(handover)` and
 * `appNowDateSql()`, so this is a plain comparison of `YYYY-MM-DD` strings (never a parse: XOL-414).
 */
export function handoverWarning (handover, handoverStatus, today) {
  if (empty(handover) || empty(handoverStatus) || empty(today)) {
    return false
  }

  return String(handover) <= String(today)
}

/** The words of that warning (decision 86), in an alert: { title, description }. */
export function handoverWarningCopy (siteName, handoverStatus) {
  return {
    title: 'This handover date is today or past.',
    description: (siteName ? siteName : 'The site') + ' becomes ' + handoverStatus + ' at the next nightly run (5:00 am Malaysia time).'
  }
}

/**
 * The Complete RTA confirm (UX v3 section 5): { icon, title, subtitle, okText, okType, width, lead, items, warning, handoverWarning, fieldLabel }.
 *   ctx { siteName, siteId, optionNo, telco, towerCo, tcClient, targetDate (as shown), others [option nos], mailPossible, unsent,
 *         handoverDate (as shown), handoverStatus, handoverPast (handoverWarning above), kept (an open RTA with a kept legacy site),
 *         transitionedStatus (the kept site has already left Candidate: decision 108),
 *         rtsOutcome ('opened' | 'linked' | 'none', XOL-693; absent or null reads as 'opened'), rtsLink (the linked RTS { id, status, open }) }
 * "{Telco} is e-mailed that the RTA is complete." is drawn only when the e-mail can go (`mailPossible`: a Telco client with Send E-mails
 * on), as in Close RTA: the UX text states it flatly, which is false for a Tower Co client or with mail off. `unsent` is
 * rtaStage.decisionUnsent (decision 49): the warning is drawn only then.
 *
 * Complete always creates (or keeps) a CANDIDATE site (decision 78); it becomes `handoverStatus` on its date (decision 86): "It becomes
 * {status} on {date}." for a future date, and for today or earlier the `handoverWarning` alert in its place (the nightly run, not the
 * save, moves it). A kept site that has already moved on (decision 108) keeps its status and date, and has neither. With no date
 * (decision 194) it "stays Candidate until a handover date is set", then becomes the status when there is one.
 */
export function completeConfirm (ctx) {
  var c = ctx || {}
  var w = names(c)
  var items = []
  var site = (c.siteName || 'The site') + (c.siteId ? ' (' + c.siteId + ')' : '')
  var moved = !!c.transitionedStatus
  var past = !moved && !!c.handoverPast
  var becomes = ''

  if (!moved && !past) {
    if (!empty(c.handoverStatus) && !empty(c.handoverDate)) {
      becomes = ' It becomes ' + c.handoverStatus + ' on ' + c.handoverDate + '.'
    } else if (empty(c.handoverDate)) {
      becomes = ' It stays Candidate until a handover date is set' + (empty(c.handoverStatus) ? '.' : ', then becomes ' + c.handoverStatus + '.')
    }
  }

  if (moved) {
    items.push('Updates ' + site + ' with the details in Step 5. It stays ' + c.transitionedStatus + ', with its handover date unchanged.')
  } else if (c.kept) {
    items.push('Keeps ' + site + ' as a Candidate site and updates it with the details in Step 5.' + becomes)
  } else {
    items.push('Creates ' + site + ' as a Candidate site from ' + (c.optionNo ? 'option ' + c.optionNo : 'the final option') + ', with the details in Step 5.' + becomes)
  }

  if (c.tcClient) {
    items.push('No RTS is opened, because ' + w.towerCo + ' is its own client.')
  } else if (c.rtsOutcome === 'linked') {
    // XOL-693: the client's existing RTS is linked; none is opened, so no target date.
    var link = c.rtsLink || null

    if (!link) {
      items.push(w.telco + '\'s existing RTS is linked to this RTA.')
    } else if (link.open) {
      items.push(w.telco + '\'s open RTS, ' + rtsRef(link.id) + (empty(link.status) ? '' : ' (at ' + link.status + ')') + ', is linked to this RTA.')
    } else {
      items.push('No new RTS is opened. ' + w.telco + '\'s closed RTS, ' + rtsRef(link.id) + ', is linked to this RTA and stays Closed.')
    }
  } else if (c.rtsOutcome === 'none') {
    items.push('No RTS is opened or linked for ' + w.telco + '.')
  } else {
    items.push('An RTS is opened for ' + w.telco + ', starting at step 1' + (c.targetDate ? ', with a target date of ' + c.targetDate : '') + '.')
  }

  var others = c.others || []

  if (others.length === 1) {
    items.push('Option ' + others[0] + ' is marked Not Selected.')
  } else if (others.length > 1) {
    items.push('Options ' + listNos(others) + ' are marked Not Selected.')
  }

  if (c.mailPossible) {
    items.push(w.telco + ' is e-mailed that the RTA is complete.')
  }

  items.push('This RTA is closed for good. It can\'t be reopened or unlocked.')

  return {
    icon: 'fa-flag',
    title: 'Complete this RTA?',
    subtitle: null,
    okText: 'Complete RTA',
    okType: 'success',
    width: '560px',
    lead: 'What happens:',
    items: items,
    warning: c.unsent ? { title: w.telco + ' hasn\'t been e-mailed this decision.', description: 'The completion e-mail tells ' + w.telco + ' the RTA is complete.' } : null,
    handoverWarning: past ? handoverWarningCopy(c.siteName, c.handoverStatus) : null,
    fieldLabel: 'Remarks (optional)'
  }
}

/**
 * The toast sentence of a completed RTA (revised UX section 8, decisions 78, 86, 108). The site is a Candidate site that becomes
 * {status} on {date} (the next nightly run for a date of today or earlier); a kept site that already moved on stays what it is. It never
 * says the site "is Active". Same ctx as completeConfirm.
 */
export function completeSentence (ctx) {
  var c = ctx || {}
  var w = names(c)
  var site = c.siteName || 'The site'
  var text = ''

  if (c.transitionedStatus) {
    text = site + ' stays ' + c.transitionedStatus + '.'
  } else if (empty(c.handoverStatus)) {
    text = empty(c.handoverDate) ? site + ' is a Candidate site with no handover date.' : site + ' is a Candidate site.'
  } else if (c.handoverPast) {
    text = site + ' is a Candidate site and becomes ' + c.handoverStatus + ' at the next nightly run.'
  } else if (empty(c.handoverDate)) {
    // A status with no date (decision 194): never a claim about the nightly run.
    text = site + ' is a Candidate site. It becomes ' + c.handoverStatus + ' once a handover date is set.'
  } else {
    text = site + ' is a Candidate site and becomes ' + c.handoverStatus + ' on ' + c.handoverDate + '.'
  }

  var rts = ' An RTS was opened for ' + w.telco + '.'

  if (c.rtsOutcome === 'linked') {
    rts = ' ' + w.telco + '\'s existing ' + (c.rtsLink ? rtsRef(c.rtsLink.id) + ' ' : 'RTS ') + 'was linked.'
  } else if (c.rtsOutcome === 'none') {
    rts = ' No RTS was opened or linked.'
  }

  return 'RTA completed. ' + text + (c.tcClient ? '' : rts)
}

/* ------------------------------------------------------------------------
   The final option block
------------------------------------------------------------------------ */

function findLabel (list, value) {
  var found = ''

  ;(list || []).forEach(function (item) {
    if (found) {
      return
    }

    if (item && String(item.value) === String(value)) {
      found = item.label || ''
    } else if (item && item.children) {
      found = findLabel(item.children, value)
    }
  })

  return found
}

/** The label of a structure type id in the config's tree ({ label, value, children }), or ''. */
export function structureLabel (list, id) {
  return blank(id) ? '' : findLabel(list, id)
}

function pathTo (list, value, trail) {
  var found = null

  ;(list || []).forEach(function (item) {
    if (found || !item) {
      return
    }

    var here = trail.concat([item.label || ''])

    if (String(item.value) === String(value)) {
      found = here
    } else if (item.children) {
      found = pathTo(item.children, value, here)
    }
  })

  return found
}

/**
 * The path of a structure type in the config's tree as "Lampole / SP" ("Parent / Child"), or ''. SelectStructure shows a value only
 * through its `disabled-text` (it reads it once, when it is created): a Step 5 form that is pre-filled with a structure type hands it
 * this, or the select would look empty over a value the site is about to be created with.
 */
export function structurePath (list, id) {
  if (blank(id)) {
    return ''
  }

  var path = pathTo(list, id, [])

  return path ? path.join(' / ') : ''
}

/**
 * The rows of the "Final option" block (UX spec 2.8, mock M-27): [{ label, value }], value '' when the option has none ("Not entered"). It is the
 * same block as Step 4's "Approved option" (RtaOptionSummary, lib/rtaDecision.approvedOptionRows): what the site and the RTS are made from.
 *   ctx { structures (the config's tree), telco, towerCo, fmtDate, client (true: no Landowner), optionNo (when the row itself could not be read) }
 */
export function finalOptionRows (option, ctx) {
  var o = option || {}
  var c = ctx || {}

  return approvedOptionRows(o, {
    structure: structureLabel(c.structures, o.structure_id),
    telco: c.telco,
    towerCo: c.towerCo,
    fmtDate: c.fmtDate,
    client: c.client,
    optionNo: c.optionNo
  })
}
